import { useResizeObserver } from '@mantine/hooks'
import {
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useState,
} from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import { LayoutContext } from '../../Contexts/LayoutContext'
import { type LayoutVariant, panelWidth } from '../../utils/constants'
import Footer from '../Footer'

const fragments = {
  AppWrapper_config: graphql(`
    fragment AppWrapper_config on CompanyConfig {
      id
      ...Footer_config
    }
  `),
}

/**
 * Enforces wrapper height based on absolutely positioned children.
 * This few JS lines make our life with css positioning much easier.
 */
const FooterWrapper = ({ children }: PropsWithChildren) => {
  const [ref, entry] = useResizeObserver()
  const [height, setHeight] = useState(0)

  // biome-ignore lint/correctness/useExhaustiveDependencies: entry is needed to trigger remeasure on resize
  useLayoutEffect(() => {
    const childHeight = ref.current?.firstElementChild?.clientHeight ?? 0
    if (childHeight > 0) {
      setHeight(childHeight)
    }
  }, [ref, entry])

  return (
    <div
      ref={ref}
      style={{
        height: `${height}px`,
      }}
    >
      {children}
    </div>
  )
}

type AppWrapperProps = {
  children?: ReactNode
  variant?: LayoutVariant
  headerComponent: ReactElement
  navbarComponent?: ReactElement
  footerComponent?: ReactElement
  config?: FragmentType<typeof fragments.AppWrapper_config>
  /** Top-align the content instead of centering it vertically in the viewport. */
  topAligned?: boolean
  hideSecuredBanner?: boolean
}

export const AppWrapper = ({
  children,
  variant = 'complex',
  headerComponent,
  navbarComponent,
  footerComponent,
  config: _config,
  topAligned = false,
  hideSecuredBanner = false,
}: AppWrapperProps) => {
  const config = getFragmentData(fragments.AppWrapper_config, _config)
  const { client } = useContext(LayoutContext)

  const showBottomText = client !== 'iframe'

  return (
    <div>
      <div
        className='w-full pt-safe-area-top min-h-screen'
        style={{ paddingBottom: client === 'app' ? 60 : 0 }}
      >
        <div className='grid grid-rows-[auto_1fr_auto_auto] min-h-[inherit]'>
          <div>{headerComponent}</div>
          <div
            className={`${topAligned ? 'mx-auto flex flex-col' : 'm-auto'} p-6 w-full min-w-0 sm:w-[75%] sm:px-[6%] md:w-[60%] md:px-[3%] lg:px-[9%] overflow-auto`}
          >
            <div>{children}</div>
            {showBottomText && (
              <div className={`${topAligned ? 'mt-auto ' : ''}pt-12`}>
                <Footer config={config} hideSecuredBanner={hideSecuredBanner} />
              </div>
            )}
          </div>
          {navbarComponent && (
            <div style={{ width: panelWidth[variant]['md'] }}>{navbarComponent}</div>
          )}
          {footerComponent && <FooterWrapper>{footerComponent}</FooterWrapper>}
        </div>
      </div>
    </div>
  )
}
